import { create } from 'zustand';
import { mockSimulationScenarios } from '../pages/Simulations/MockData';
import type { SimulationModification, SimulationResults, SimulationScenario } from '../types';

interface SimulationState {
  // Scenarios
  scenarios: SimulationScenario[];
  activeScenario: SimulationScenario | null;

  // Selection for comparison
  comparisonScenarios: string[];

  // Running state
  isRunning: boolean;
  runProgress: number;

  // Actions
  loadScenarios: () => void;
  setActiveScenario: (scenarioId: string | null) => void;

  // CRUD Operations
  createScenario: (data: Omit<SimulationScenario, 'id' | 'status' | 'createdAt' | 'createdBy'>) => SimulationScenario;
  updateScenario: (id: string, data: Partial<SimulationScenario>) => void;
  deleteScenario: (id: string) => void;
  duplicateScenario: (id: string, newName: string) => SimulationScenario | null;

  // Modifications
  addModification: (scenarioId: string, modification: SimulationModification) => void;
  updateModification: (scenarioId: string, index: number, modification: SimulationModification) => void;
  removeModification: (scenarioId: string, index: number) => void;

  // Simulation Running
  runSimulation: (scenarioId: string) => Promise<void>;
  cancelSimulation: (scenarioId: string) => void;

  // Comparison
  addToComparison: (scenarioId: string) => void;
  removeFromComparison: (scenarioId: string) => void;
  clearComparison: () => void;

  // Promotion
  promoteToLive: (scenarioId: string) => void;

  // Query helpers
  getScenarioById: (id: string) => SimulationScenario | undefined;
  getScenariosByStatus: (status: SimulationScenario['status']) => SimulationScenario[];
  getComparisonData: () => SimulationScenario[];
}

// Generate unique ID
const generateId = (): string => `sim-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Simulate running a scenario (mock)
const simulateRun = async (
  onProgress: (progress: number) => void,
): Promise<SimulationResults> => {
  // Simulate progress updates
  for (let i = 0; i <= 100; i += 10) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    onProgress(i);
  }

  // Return mock results
  const feasible = Math.random() > 0.3;
  return {
    feasible,
    conflicts: [],
    stats: {
      totalLessons: 248,
      conflictCount: feasible ? Math.floor(Math.random() * 5) : Math.floor(Math.random() * 10) + 5,
      ruleViolations: {
        hard: feasible ? 0 : Math.floor(Math.random() * 3) + 1,
        soft: Math.floor(Math.random() * 10) + 5,
      },
      curriculumCoverage: 95 + Math.random() * 5,
      teacherLoad: [],
      roomUtilization: [],
      teacherPreferenceSatisfaction: 80 + Math.random() * 15,
      distributionScore: 85 + Math.random() * 10,
    },
    comparison: {
      conflictDelta: Math.floor(Math.random() * 10) - 5,
      teacherLoadDelta: Math.floor(Math.random() * 10) - 5,
      roomUtilizationDelta: Math.floor(Math.random() * 10) - 5,
      curriculumCoverageDelta: (Math.random() * 4) - 2,
    },
    recommendations: feasible
      ? ['Scheduling optimized successfully', 'Consider minor adjustments to PE slots']
      : ['Additional resources needed', 'Review teacher availability constraints'],
    requiredChanges: feasible ? [] : ['Hire additional staff', 'Adjust room allocations'],
  };
};

export const useSimulationStore = create<SimulationState>((set, get) => ({
  // Initial state
  scenarios: mockSimulationScenarios,
  activeScenario: null,
  comparisonScenarios: [],
  isRunning: false,
  runProgress: 0,

  // Load scenarios (refresh from mock data)
  loadScenarios: () => {
    set({ scenarios: [...mockSimulationScenarios] });
  },

  // Set active scenario
  setActiveScenario: (scenarioId) => {
    const scenario = scenarioId ? get().scenarios.find((s) => s.id === scenarioId) : null;
    set({ activeScenario: scenario || null });
  },

  // Create new scenario
  createScenario: (data) => {
    const newScenario: SimulationScenario = {
      ...data,
      id: generateId(),
      status: 'draft',
      createdAt: new Date().toISOString(),
      createdBy: 'admin-1', // In real app, this would be current user
    };

    set((state) => ({
      scenarios: [...state.scenarios, newScenario],
      activeScenario: newScenario,
    }));

    return newScenario;
  },

  // Update scenario
  updateScenario: (id, data) => {
    set((state) => ({
      scenarios: state.scenarios.map((s) => (s.id === id ? { ...s, ...data } : s)),
      activeScenario: state.activeScenario?.id === id ? { ...state.activeScenario, ...data } : state.activeScenario,
    }));
  },

  // Delete scenario
  deleteScenario: (id) => {
    set((state) => ({
      scenarios: state.scenarios.filter((s) => s.id !== id),
      activeScenario: state.activeScenario?.id === id ? null : state.activeScenario,
      comparisonScenarios: state.comparisonScenarios.filter((cid) => cid !== id),
    }));
  },

  // Duplicate scenario
  duplicateScenario: (id, newName) => {
    const original = get().scenarios.find((s) => s.id === id);
    if (!original) return null;

    const duplicate: SimulationScenario = {
      ...original,
      id: generateId(),
      name: newName,
      status: 'draft',
      results: undefined,
      createdAt: new Date().toISOString(),
      lastRun: undefined,
    };

    set((state) => ({
      scenarios: [...state.scenarios, duplicate],
    }));

    return duplicate;
  },

  // Add modification to scenario
  addModification: (scenarioId, modification) => {
    set((state) => ({
      scenarios: state.scenarios.map((s) =>
        s.id === scenarioId
          ? { ...s, modifications: [...s.modifications, modification], status: 'draft' as const }
          : s,
      ),
    }));
  },

  // Update modification
  updateModification: (scenarioId, index, modification) => {
    set((state) => ({
      scenarios: state.scenarios.map((s) => {
        if (s.id !== scenarioId) return s;
        const newMods = [...s.modifications];
        newMods[index] = modification;
        return { ...s, modifications: newMods, status: 'draft' as const };
      }),
    }));
  },

  // Remove modification
  removeModification: (scenarioId, index) => {
    set((state) => ({
      scenarios: state.scenarios.map((s) => {
        if (s.id !== scenarioId) return s;
        const newMods = s.modifications.filter((_, i) => i !== index);
        return { ...s, modifications: newMods, status: 'draft' as const };
      }),
    }));
  },

  // Run simulation
  runSimulation: async (scenarioId) => {
    const scenario = get().scenarios.find((s) => s.id === scenarioId);
    if (!scenario || scenario.status === 'running') return;

    // Set running state
    set((state) => ({
      isRunning: true,
      runProgress: 0,
      scenarios: state.scenarios.map((s) => (s.id === scenarioId ? { ...s, status: 'running' as const } : s)),
    }));

    try {
      // Run simulation with progress updates
      const results = await simulateRun((progress) => {
        set({ runProgress: progress });
      });

      // Update with results
      set((state) => ({
        isRunning: false,
        runProgress: 100,
        scenarios: state.scenarios.map((s) =>
          s.id === scenarioId
            ? {
                ...s,
                status: 'completed' as const,
                results,
                lastRun: new Date().toISOString(),
              }
            : s,
        ),
      }));
    } catch {
      // Handle failure
      set((state) => ({
        isRunning: false,
        runProgress: 0,
        scenarios: state.scenarios.map((s) => (s.id === scenarioId ? { ...s, status: 'failed' as const } : s)),
      }));
    }
  },

  // Cancel simulation
  cancelSimulation: (scenarioId) => {
    set((state) => ({
      isRunning: false,
      runProgress: 0,
      scenarios: state.scenarios.map((s) => (s.id === scenarioId ? { ...s, status: 'draft' as const } : s)),
    }));
  },

  // Comparison management
  addToComparison: (scenarioId) => {
    set((state) => {
      if (state.comparisonScenarios.includes(scenarioId)) return state;
      if (state.comparisonScenarios.length >= 3) return state; // Max 3 scenarios
      return { comparisonScenarios: [...state.comparisonScenarios, scenarioId] };
    });
  },

  removeFromComparison: (scenarioId) => {
    set((state) => ({
      comparisonScenarios: state.comparisonScenarios.filter((id) => id !== scenarioId),
    }));
  },

  clearComparison: () => {
    set({ comparisonScenarios: [] });
  },

  // Promote scenario to live timetable
  promoteToLive: (scenarioId) => {
    const scenario = get().scenarios.find((s) => s.id === scenarioId);
    if (!scenario || !scenario.results?.feasible) return;

    // In real app, this would update the main timetable
    console.log('Promoting scenario to live:', scenarioId);

    // Mark as promoted (you could add a 'promoted' status)
    set((state) => ({
      scenarios: state.scenarios.map((s) =>
        s.id === scenarioId ? { ...s, description: `${s.description} [PROMOTED TO LIVE]` } : s,
      ),
    }));
  },

  // Query helpers
  getScenarioById: (id) => {
    return get().scenarios.find((s) => s.id === id);
  },

  getScenariosByStatus: (status) => {
    return get().scenarios.filter((s) => s.status === status);
  },

  getComparisonData: () => {
    const { scenarios, comparisonScenarios } = get();
    return scenarios.filter((s) => comparisonScenarios.includes(s.id));
  },
}));

